import {
  type KeyboardEvent, type MouseEvent,
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Badge, Box, Button, Chip, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import type { CellTrace, ColumnLookup, SavedFilter } from './types';
import type { ColumnLookupRow } from './api';
import TracePanel from './TracePanel';
import LookupPickerDialog from './LookupPickerDialog';
import { compileFilter, FilterCompileError } from './FilterExpression';
import FiltersManagerDialog from './FiltersManagerDialog';
import ColumnSettingsDialog from './ColumnSettingsDialog';

// AG Grid v33+ requires explicit module registration and uses the Theming API
// (no CSS-file themes). Registering the all-community bundle once at import is
// idempotent. The grid's VALUE exports resolve fine.
ModuleRegistry.registerModules([AllCommunityModule]);

/* AG Grid's npm package lists the `import` (ESM) condition before `types` in
 * its package.json `exports`, so under this project's `moduleResolution:
 * "bundler"` TypeScript resolves the JS entry — which carries the runtime
 * (value) exports but NOT the type-only re-exports (ColDef, GridApi, the event
 * interfaces, …). Rather than weaken the whole app's tsconfig, we alias the few
 * grid types this vendored component annotates with. This affects only
 * compile-time typing inside this file; the grid behaves identically at runtime.
 * eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ColDef = any            // eslint-disable-line @typescript-eslint/no-explicit-any
type GridApi = any           // eslint-disable-line @typescript-eslint/no-explicit-any
type ICellRendererParams = Record<string, any>  // eslint-disable-line @typescript-eslint/no-explicit-any
type IRowNode = any          // eslint-disable-line @typescript-eslint/no-explicit-any
type CellClickedEvent = any  // eslint-disable-line @typescript-eslint/no-explicit-any
type CellValueChangedEvent = any  // eslint-disable-line @typescript-eslint/no-explicit-any
type ColumnResizedEvent = any  // eslint-disable-line @typescript-eslint/no-explicit-any
type CustomHeaderProps = any      // eslint-disable-line @typescript-eslint/no-explicit-any
type CustomCellEditorProps = any  // eslint-disable-line @typescript-eslint/no-explicit-any

// A normalised rectangular cell range (displayed-row indices × data-column
// indices), inclusive on every side.
interface RangeBounds { r1: number; r2: number; c1: number; c2: number; }
function normRange(
  range: { a: { r: number; c: number }; f: { r: number; c: number } },
): RangeBounds {
  return {
    r1: Math.min(range.a.r, range.f.r), r2: Math.max(range.a.r, range.f.r),
    c1: Math.min(range.a.c, range.f.c), c2: Math.max(range.a.c, range.f.c),
  };
}
function toNumOrRaw(v: string): number | string | null {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
}

// Mirror angular's getCurrentColLabel: snake_case -> Title Case for display,
// while keeping the raw field name for filtering / formulas.
function humanizeHeader(col: string): string {
  return col
    .replace(/[_\s]+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

function isNumericValue(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'boolean') return false;
  if (typeof v === 'string') {
    const t = v.trim();
    if (!t) return false;
    return !Number.isNaN(Number(t));
  }
  return false;
}

function detectNumericColumns(cols: string[], rows: Record<string, unknown>[]): Set<string> {
  const out = new Set<string>();
  const sample = rows.slice(0, Math.min(rows.length, 50));
  for (const c of cols) {
    let seen = 0;
    let numeric = 0;
    for (const r of sample) {
      const v = r?.[c];
      if (v === null || v === undefined || v === '') continue;
      seen += 1;
      if (isNumericValue(v)) numeric += 1;
    }
    if (seen > 0 && numeric === seen) out.add(c);
  }
  return out;
}

function formatNumber(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  const abs = Math.abs(n);
  const isInt = Number.isInteger(n);
  if (isInt) return n.toLocaleString();
  const fractionDigits = abs >= 1 ? 2 : 4;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

// Which preview columns are editable. 'all' = every column; otherwise a map of
// (lower-cased) column name -> spec, used to pick the right cell editor and
// (optionally) offer a reference-table lookup picker.
export interface EditableColSpec {
  type: string;
  /** When set, the cell offers a 🔍 lookup picker (Review tab). */
  lookup?: ColumnLookup;
}
export type EditableSpec = 'all' | Record<string, EditableColSpec>;

// Synthetic per-row id, injected so AG Grid selection/edits survive re-renders.
const RID = '__rid';

function editorFor(type: string | undefined): string {
  const t = (type || '').toLowerCase();
  if (['int', 'integer', 'bigint', 'decimal', 'float', 'number', 'numeric'].includes(t)) {
    return 'agNumberCellEditor';
  }
  if (['date', 'datetime', 'timestamp'].includes(t)) return 'agDateStringCellEditor';
  return 'agTextCellEditor';
}

// Loose equality so a grid edit (often a string) compares cleanly to the
// original typed value: nulls/blanks are equal; numerics compare numerically.
function valuesEqual(a: unknown, b: unknown): boolean {
  const na = a === null || a === undefined || a === '';
  const nb = b === null || b === undefined || b === '';
  if (na || nb) return na && nb;
  const an = Number(a);
  const bn = Number(b);
  if (!Number.isNaN(an) && !Number.isNaN(bn)) return an === bn;
  return String(a) === String(b);
}

interface EditEntry {
  rid: number;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

interface GridCellContext {
  openTrace?: (rowIndex: number, column: string) => void;
  revertCell?: (rid: number, column: string) => void;
  openLookup?: (rid: number, column: string) => void;
  getOriginal?: (rid: number, column: string) => unknown;
  valuesEqual?: (a: unknown, b: unknown) => boolean;
}

// Renders a cell's value plus hover-only action icons:
//   - "explain" (info) on calc cells -> opens the trace panel. This is the only
//     way to open the panel for editable calc columns, where a plain click must
//     focus/edit the cell instead.
//   - "revert" (restore) on editable cells whose value differs from the
//     original preview value -> restores that cell and recalculates.
// Handlers come through the grid ``context``.
function GridCellRenderer(
  params: ICellRendererParams & { explain?: boolean; editable?: boolean; hasLookup?: boolean },
) {
  const text = params.valueFormatted ?? (params.value == null ? '' : String(params.value));
  const ctx = (params.context || {}) as GridCellContext;
  const rid = params.data ? (params.data[RID] as number) : undefined;
  const col = params.colDef && 'field' in params.colDef ? params.colDef.field : undefined;

  const dirty = !!params.editable && rid != null && !!col && !!ctx.getOriginal && !!ctx.valuesEqual
    && !ctx.valuesEqual(params.value, ctx.getOriginal(rid, col));

  const explain = (e: MouseEvent) => {
    e.stopPropagation();
    const idx = params.node?.rowIndex;
    if (idx != null && col) ctx.openTrace?.(idx, col);
  };
  const revert = (e: MouseEvent) => {
    e.stopPropagation();
    if (rid != null && col) ctx.revertCell?.(rid, col);
  };
  const lookup = (e: MouseEvent) => {
    e.stopPropagation();
    if (rid != null && col) ctx.openLookup?.(rid, col);
  };
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <span className="gp-calc-cellwrap">
      <span className="gp-cell-val">{text}</span>
      {params.hasLookup && (
        <button type="button" className="gp-lookup-btn" onClick={lookup}
          onMouseDown={stop} title="Look up a value" tabIndex={-1}>
          <SearchIcon sx={{ fontSize: 15 }} />
        </button>
      )}
      {dirty && (
        <button type="button" className="gp-revert-btn" onClick={revert}
          onMouseDown={stop} title="Revert to original value" tabIndex={-1}>
          <RestoreIcon sx={{ fontSize: 15 }} />
        </button>
      )}
      {params.explain && (
        <button type="button" className="gp-explain-btn" onClick={explain}
          onMouseDown={stop} title="Explain this value" tabIndex={-1}>
          <InfoOutlinedIcon sx={{ fontSize: 15 }} />
        </button>
      )}
    </span>
  );
}

// ---- Custom column header: label + sort indicator + a funnel filter icon
// that stays hidden until the header is hovered/focused (req: "filter icon shows
// up when the user focuses on the heading"). Clicking it opens that column's
// built-in filter popup. The grid keeps its own chrome (resize, keyboard nav)
// around this component, so we only re-implement sort + filter affordances.
function safeSort(column: any): string | null {            // eslint-disable-line @typescript-eslint/no-explicit-any
  try { return column?.getSort?.() ?? null; } catch { return null; }
}
function safeFilterActive(column: any): boolean {           // eslint-disable-line @typescript-eslint/no-explicit-any
  try { return column?.isFilterActive?.() ?? false; } catch { return false; }
}
function GpColumnHeader(props: CustomHeaderProps) {
  const { displayName, column, enableSorting, enableFilterButton, progressSort, showFilter } = props;
  const filterRef = useRef<HTMLButtonElement>(null);
  const [sortDir, setSortDir] = useState<string | null>(() => safeSort(column));
  const [filterActive, setFilterActive] = useState<boolean>(() => safeFilterActive(column));

  useEffect(() => {
    const onSort = () => setSortDir(safeSort(column));
    const onFilter = () => setFilterActive(safeFilterActive(column));
    column?.addEventListener?.('sortChanged', onSort);
    column?.addEventListener?.('filterChanged', onFilter);
    column?.addEventListener?.('filterActiveChanged', onFilter);
    return () => {
      column?.removeEventListener?.('sortChanged', onSort);
      column?.removeEventListener?.('filterChanged', onFilter);
      column?.removeEventListener?.('filterActiveChanged', onFilter);
    };
  }, [column]);

  const colDef = column?.getColDef?.() || {};
  const numeric = colDef.type === 'numericColumn';
  const canFilter = enableFilterButton || !!colDef.filter;

  const onLabelClick = (e: MouseEvent) => { if (enableSorting) progressSort?.(e.shiftKey); };
  const onFilterClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (filterRef.current) showFilter?.(filterRef.current);
  };

  return (
    <div
      className="gp-h-root"
      style={{
        display: 'flex', alignItems: 'center', width: '100%', gap: 2,
        justifyContent: numeric ? 'flex-end' : 'flex-start',
      }}
    >
      <div
        className="gp-h-label"
        onClick={onLabelClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, minWidth: 0,
          flex: numeric ? '0 1 auto' : 1,
          cursor: enableSorting ? 'pointer' : 'default',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </span>
        {sortDir === 'asc' && <span className="gp-h-sort">▲</span>}
        {sortDir === 'desc' && <span className="gp-h-sort">▼</span>}
      </div>
      {canFilter && (
        <button
          ref={filterRef}
          type="button"
          className={`gp-h-filter${filterActive ? ' active' : ''}`}
          onClick={onFilterClick}
          tabIndex={-1}
          title="Filter this column"
          aria-label="filter column"
        >
          <FilterAltIcon sx={{ fontSize: 15 }} />
        </button>
      )}
    </div>
  );
}

// ---- Custom cell editor for editable columns that carry a reference-table
// lookup: a plain input plus an inline 🔍 button so the picker is reachable
// WHILE the cell is being edited (req). The button hands off to the table-level
// lookup dialog via the grid context (so the dialog survives the cell losing
// focus); the typed value is committed first.
function LookupCellEditor(props: CustomCellEditorProps) {
  const { value, onValueChange, stopEditing, eventKey, context, data, colDef, column, numeric } = props;
  const startedTyping = !!(eventKey && String(eventKey).length === 1);
  const initial = startedTyping ? String(eventKey) : (value == null ? '' : String(value));
  const [val, setVal] = useState<string>(initial);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    if (!startedTyping) inputRef.current?.select?.();
    onValueChange?.(numeric ? toNumOrRaw(initial) : initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (v: string) => {
    setVal(v);
    onValueChange?.(numeric ? toNumOrRaw(v) : v);
  };

  const openLookup = () => {
    const rid = data ? (data[RID] as number) : undefined;
    const col = (colDef && 'field' in colDef ? colDef.field : undefined) || column?.getColId?.();
    stopEditing?.();
    if (rid != null && col) context?.openLookup?.(rid, col);
  };

  return (
    <div className="gp-lk-editor" style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
      <input
        ref={inputRef}
        type={numeric ? 'number' : 'text'}
        value={val}
        onChange={(e) => update(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') stopEditing?.();
          else if (e.key === 'Escape') stopEditing?.(true);
        }}
        style={{
          flex: 1, minWidth: 0, height: '100%', border: 'none', outline: 'none',
          background: 'transparent', color: 'inherit', font: 'inherit', padding: '0 8px',
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        className="gp-lk-ed-btn"
        title="Look up a value"
        onMouseDown={(e) => e.preventDefault()}
        onClick={openLookup}
      >
        <SearchIcon sx={{ fontSize: 15 }} />
      </button>
    </div>
  );
}

interface Props {
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  calcColumns?: string[];
  /** map row_index -> { calc_col -> trace }. */
  traces?: Record<string, Record<string, CellTrace>>;
  /** previous preview's rows, same shape — used to highlight changed cells. */
  previousRows?: Record<string, unknown>[];
  /** Number of calculations in the process, used to enable Move down. */
  calcCount?: number;
  /** Index (within proc.calculations) of the calc that produced ``column``. */
  calcIndexByColumn?: (column: string) => number;
  onEditCalc?: (column: string) => void;
  onDeleteCalc?: (column: string) => void;
  onMoveCalc?: (column: string, delta: number) => void;
  /** Show the sticky checkbox selection column (MultiRowSelect). */
  enableSelection?: boolean;
  /** Which columns are editable (columnEditing). Undefined = none editable. */
  editableColumns?: EditableSpec;
  /** Lower-cased columns made read-only for this organization (screen
   *  customization). Narrows `editableColumns`; never widens it. */
  lockedColumns?: Set<string>;
  /** Selected rows changed (data objects, RID stripped, edits baked in). */
  onSelectionChange?: (rows: Record<string, unknown>[]) => void;
  /** A cell was edited; receives all current rows (RID stripped). */
  onRowsEdited?: (rows: Record<string, unknown>[]) => void;
  /**
   * Recalculate a single edited row. Given the row (RID stripped) and the
   * edited column, returns the row with downstream calc columns refreshed.
   * Used to propagate an edit into dependent columns.
   */
  recalcRow?: (
    row: Record<string, unknown>,
    editedColumn: string,
  ) => Promise<Record<string, unknown>>;
  /**
   * Search a column's reference table for the inline lookup picker. Given the
   * grid column name, the search text, and the current row (for {col} filter
   * placeholders), returns matching display/value rows. Required for any
   * editable column whose spec carries a ``lookup``.
   */
  fetchLookup?: (
    column: string,
    search: string,
    row: Record<string, unknown>,
  ) => Promise<ColumnLookupRow[]>;
  /**
   * Persisted column widths keyed by column (field) name. Columns listed here
   * render at the given fixed width; others flex to fill the grid.
   */
  columnWidths?: Record<string, number>;
  /**
   * Called when the user drag-resizes one or more columns, with the merged
   * width map (existing widths plus the just-resized columns). The parent
   * stores this on the process so the widths survive save/reload.
   */
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  /**
   * Custom display headings keyed by column (field) name. A column absent here
   * falls back to the humanized field name. Covers source AND calc columns —
   * the parent merges calc headers into this map.
   */
  columnHeaders?: Record<string, string>;
  /**
   * Set/clear a SOURCE column heading (empty string clears the override). Only
   * offered for non-calculated columns; calc headings are edited via onEditCalc.
   */
  onColumnHeaderChange?: (field: string, header: string) => void;
  /**
   * Named row filters persisted on the process. When omitted, the table keeps
   * its own local filter set (no persistence — e.g. Builder mode).
   */
  savedFilters?: SavedFilter[];
  /** Emits the full next filter set when the user adds/edits/toggles/deletes. */
  onSavedFiltersChange?: (filters: SavedFilter[]) => void;
  /** Cells (``"rowIndex:colLower"``) flagged by a validation report — tinted red. */
  errorCells?: Set<string>;
  /** A cell to scroll to + flash (validation tracing). ``n`` bumps to re-fire. */
  focusCell?: { row: number; col: string; n: number } | null;
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function PreviewTable({
  columns, rows, rowCount, calcColumns, traces, previousRows,
  calcCount, calcIndexByColumn, onEditCalc, onDeleteCalc, onMoveCalc,
  enableSelection, editableColumns, lockedColumns, onSelectionChange, onRowsEdited, recalcRow,
  fetchLookup, columnWidths, onColumnWidthsChange,
  columnHeaders, onColumnHeaderChange, savedFilters, onSavedFiltersChange,
  errorCells, focusCell,
}: Props) {
  type TraceFrame = { rowIndex: number; column: string; trace: CellTrace };
  // item_change protect flags, keyed by RID → set of locked (lower-cased) cols.
  // Read live by the editable callback so a lock takes effect without rebuild.
  const protectRef = useRef<Map<number, Set<string>>>(new Map());
  const [traceStack, setTraceStack] = useState<TraceFrame[]>([]);
  const activeTrace = traceStack.length ? traceStack[traceStack.length - 1] : null;

  // ----- Named row filters -----
  // The saved filters are controlled by the parent when onSavedFiltersChange is
  // supplied; otherwise the table keeps a local set (Builder mode has no save).
  const [localFilters, setLocalFilters] = useState<SavedFilter[]>(savedFilters ?? []);
  useEffect(() => {
    if (savedFilters) setLocalFilters(savedFilters);
  }, [savedFilters]);
  const filters = savedFilters ?? localFilters;
  const updateFilters = useCallback((next: SavedFilter[]) => {
    setLocalFilters(next);
    onSavedFiltersChange?.(next);
  }, [onSavedFiltersChange]);

  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [filterErrors, setFilterErrors] = useState<string[]>([]);
  const [filterWarnings, setFilterWarnings] = useState<string[]>([]);
  const predicateRef = useRef<(r: Record<string, unknown>) => boolean>(() => true);
  const gridApiRef = useRef<GridApi | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(rows.length);

  // ----- Excel-like cell range (drag-select) + clipboard -----
  // The DOM root of the grid (for locating .ag-cell elements by col-id/row-index)
  // and the current rectangular range (anchor + focus, in displayed-row × data-
  // column index space). A 1×1 range is treated as "no range" for painting.
  const gridBoxRef = useRef<HTMLDivElement | null>(null);
  const rangeRef = useRef<{ a: { r: number; c: number }; f: { r: number; c: number } } | null>(null);
  const draggingRef = useRef(false);
  const [rangeVersion, setRangeVersion] = useState(0);
  const bumpRange = useCallback(() => setRangeVersion((v) => v + 1), []);
  const clearRange = useCallback(() => { rangeRef.current = null; bumpRange(); }, [bumpRange]);

  // Enabled filters with a non-empty expression actually narrow the rows.
  const activeFilters = useMemo(
    () => filters.filter((f) => f.enabled && f.expression.trim()),
    [filters],
  );

  // Inline lookup picker target: which cell (rid + column) is being looked up,
  // plus a snapshot of its row so the filter's {col} placeholders resolve.
  const [lookupTarget, setLookupTarget] = useState<{
    rid: number;
    column: string;
    lookup: ColumnLookup;
    current: unknown;
    row: Record<string, unknown>;
  } | null>(null);

  // Full-screen: render the table as a fixed overlay filling the viewport.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // Compile every enabled filter; a row passes only if it satisfies ALL of them
  // (AND). Compile errors/warnings are collected per filter name for display.
  useEffect(() => {
    const preds: ((r: Record<string, unknown>) => boolean)[] = [];
    const errs: string[] = [];
    const warns: string[] = [];
    for (const f of activeFilters) {
      try {
        const compiled = compileFilter(f.expression, columns);
        preds.push(compiled.predicate);
        if (compiled.warning) warns.push(`${f.name}: ${compiled.warning}`);
      } catch (e) {
        const msg = e instanceof FilterCompileError ? e.message : (e as Error).message;
        errs.push(`${f.name}: ${msg}`);
      }
    }
    predicateRef.current = (row) => preds.every((p) => p(row));
    setFilterErrors(errs);
    setFilterWarnings(warns);
    gridApiRef.current?.onFilterChanged();
  }, [activeFilters, columns]);

  const isExternalFilterPresent = useCallback(() => activeFilters.length > 0, [activeFilters]);
  const doesExternalFilterPass = useCallback(
    (node: IRowNode) => predicateRef.current(node.data || {}),
    [],
  );

  const onFilterChanged = useCallback(() => {
    setVisibleCount(gridApiRef.current?.getDisplayedRowCount?.() ?? rows.length);
  }, [rows.length]);

  // Recount when underlying data changes too.
  useEffect(() => {
    setVisibleCount(rows.length);
  }, [rows]);

  // Close panel + drop any cell range when underlying data changes (new preview).
  useEffect(() => { setTraceStack([]); rangeRef.current = null; setRangeVersion((v) => v + 1); }, [rows, traces]);

  // Validation tracing: scroll to + flash the cell the user clicked in the
  // report. `focusCell.row` is the ORIGINAL preview-row index (= RID); resolve
  // its current displayed position so sort/filter don't misplace it.
  useEffect(() => {
    if (!focusCell) return;
    const api = gridApiRef.current;
    if (!api) return;
    try {
      const node = api.getRowNode(String(focusCell.row));
      if (node && node.rowIndex != null) {
        api.ensureIndexVisible(node.rowIndex, 'middle');
        if (focusCell.col) api.flashCells({ rowNodes: [node], columns: [focusCell.col] });
      }
    } catch {
      // Row may be outside the current preview (e.g. limited row count).
    }
  }, [focusCell]);

  const calcSet = useMemo(() => new Set((calcColumns || []).map((c) => c.toLowerCase())), [calcColumns]);
  const numericSet = useMemo(() => detectNumericColumns(columns, rows), [columns, rows]);

  // Distinct sample values per column for the filter builder's value autocomplete.
  const distinctValues = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const col of columns) {
      const set = new Set<string>();
      for (const r of rows) {
        const v = r?.[col];
        if (v === null || v === undefined || v === '') continue;
        set.add(String(v));
        if (set.size >= 500) break;
      }
      m.set(col, [...set].sort((a, b) => a.localeCompare(b)));
    }
    return m;
  }, [columns, rows]);

  // Toggle a single saved filter on/off straight from its toolbar chip.
  const toggleFilter = useCallback((name: string) => {
    updateFilters(filters.map((f) => (f.name === name ? { ...f, enabled: !f.enabled } : f)));
  }, [filters, updateFilters]);

  // Set a column's fixed width (or revert to auto) from the column-settings
  // dialog; merges into the existing width map and reports it to the parent.
  const setColumnWidth = useCallback((field: string, width: number | undefined) => {
    if (!onColumnWidthsChange) return;
    const next = { ...(columnWidths || {}) };
    if (width === undefined) delete next[field];
    else next[field] = width;
    onColumnWidthsChange(next);
  }, [onColumnWidthsChange, columnWidths]);

  const prevByKey = useMemo(() => {
    if (!previousRows) return null;
    return previousRows;
  }, [previousRows]);

  // Inject a stable id so selection/edits survive grid re-renders. AG Grid
  // mutates these objects in place on edit, so reading them back gives the
  // edited values.
  const gridRows = useMemo(
    () => rows.map((r, i) => ({ ...r, [RID]: i })),
    [rows],
  );

  // Whether a given column can be edited (and which type drives the editor,
  // plus any reference-table lookup attached to it).
  const isColEditable = useCallback(
    (col: string): { editable: boolean; type?: string; lookup?: ColumnLookup } => {
      if (!editableColumns) return { editable: false };
      if (lockedColumns?.has(col.toLowerCase())) return { editable: false };
      if (editableColumns === 'all') {
        return { editable: true, type: numericSet.has(col) ? 'number' : 'string' };
      }
      const spec = editableColumns[col.toLowerCase()];
      return spec != null
        ? { editable: true, type: spec.type, lookup: spec.lookup }
        : { editable: false };
    },
    [editableColumns, lockedColumns, numericSet],
  );
  const editingEnabled = !!editableColumns;

  const stripRid = useCallback((r: Record<string, unknown>) => {
    const rest = { ...r };
    delete rest[RID];
    return rest;
  }, []);

  // Pull item_change protect flags out of a recalc result into protectRef
  // (so the cells lock), and strip the smuggled key before it reaches the row.
  const applyProtect = useCallback((
    rid: number | undefined, updated: Record<string, unknown>,
  ): Record<string, unknown> => {
    const prot = updated.__protect as Record<string, 0 | 1> | undefined;
    if (prot && rid != null) {
      const set = protectRef.current.get(rid) || new Set<string>();
      for (const [c, on] of Object.entries(prot)) {
        const lc = c.toLowerCase();
        if (on) set.add(lc); else set.delete(lc);
      }
      protectRef.current.set(rid, set);
    }
    delete updated.__protect;
    return updated;
  }, []);

  const emitSelection = useCallback(() => {
    if (!onSelectionChange) return;
    const sel = gridApiRef.current?.getSelectedRows() ?? [];
    onSelectionChange(sel.map(stripRid));
  }, [onSelectionChange, stripRid]);

  const emitAllRows = useCallback(() => {
    if (!onRowsEdited || !gridApiRef.current) return;
    const out: Record<string, unknown>[] = [];
    gridApiRef.current.forEachNode((n: IRowNode) => { if (n.data) out.push(stripRid(n.data)); });
    onRowsEdited(out);
  }, [onRowsEdited, stripRid]);

  // ----- Edit history (undo / redo / revert) ------------------------------
  // Whole-row snapshots so undo/redo restore recalculated dependents too, not
  // just the directly-edited cell (AG Grid's built-in undo can't see the
  // setData we use to apply recalc results).
  const originalByRidRef = useRef<Map<number, Record<string, unknown>>>(new Map());
  const undoRef = useRef<EditEntry[]>([]);
  const redoRef = useRef<EditEntry[]>([]);
  const [histVersion, setHistVersion] = useState(0);

  // Rebuild originals and clear history whenever a fresh preview loads.
  useEffect(() => {
    const m = new Map<number, Record<string, unknown>>();
    for (const r of gridRows) m.set(r[RID] as number, { ...r });
    originalByRidRef.current = m;
    undoRef.current = [];
    redoRef.current = [];
    setHistVersion((v) => v + 1);
  }, [gridRows]);

  const getOriginal = useCallback(
    (rid: number, column: string) => originalByRidRef.current.get(rid)?.[column],
    [],
  );

  // Push the recalculated/edited values for a row onto a node and re-emit.
  const applyRowState = useCallback(
    (rid: number, data: Record<string, unknown>) => {
      const node = gridApiRef.current?.getRowNode(String(rid));
      if (!node) return;
      node.setData({ ...data, [RID]: rid });
      if (enableSelection) emitSelection();
      emitAllRows();
    },
    [enableSelection, emitSelection, emitAllRows],
  );

  const recordEdit = useCallback((entry: EditEntry) => {
    undoRef.current.push(entry);
    redoRef.current = [];           // a new edit invalidates the redo branch
    setHistVersion((v) => v + 1);
  }, []);

  const undo = useCallback(() => {
    const entry = undoRef.current.pop();
    if (!entry) return;
    redoRef.current.push(entry);
    applyRowState(entry.rid, entry.before);
    setHistVersion((v) => v + 1);
  }, [applyRowState]);

  const redo = useCallback(() => {
    const entry = redoRef.current.pop();
    if (!entry) return;
    undoRef.current.push(entry);
    applyRowState(entry.rid, entry.after);
    setHistVersion((v) => v + 1);
  }, [applyRowState]);

  // Restore a single cell to its original value and recompute dependents.
  const revertCell = useCallback(async (rid: number, column: string) => {
    const node = gridApiRef.current?.getRowNode(String(rid));
    const orig = originalByRidRef.current.get(rid);
    if (!node || !orig) return;
    const before = { ...node.data };
    let merged: Record<string, unknown> = { ...node.data, [column]: orig[column] };
    if (recalcRow) {
      try {
        const updated = applyProtect(rid, await recalcRow(stripRid(merged), column));
        merged = { ...merged, ...updated };
      } catch {
        // Parent surfaces the error; still apply the cell revert.
      }
    }
    node.setData({ ...merged, [RID]: rid });
    recordEdit({ rid, before, after: { ...node.data } });
    if (enableSelection) emitSelection();
    emitAllRows();
  }, [recalcRow, stripRid, recordEdit, enableSelection, emitSelection, emitAllRows]);

  // Open the lookup picker for an editable cell that carries a lookup spec.
  const openLookup = useCallback((rid: number, column: string) => {
    const spec = isColEditable(column);
    if (!spec.editable || !spec.lookup) return;
    const node = gridApiRef.current?.getRowNode(String(rid));
    setLookupTarget({
      rid,
      column,
      lookup: spec.lookup,
      current: node?.data?.[column],
      row: node?.data ? stripRid(node.data) : {},
    });
  }, [isColEditable, stripRid]);

  // Write a picked lookup value into a cell and recompute dependents — the
  // same path a manual edit takes (selection + history + emit).
  const applyLookupValue = useCallback(async (rid: number, column: string, value: unknown) => {
    const node = gridApiRef.current?.getRowNode(String(rid));
    if (!node) return;
    const before = { ...node.data };
    let merged: Record<string, unknown> = { ...node.data, [column]: value };
    if (recalcRow) {
      try {
        const updated = applyProtect(rid, await recalcRow(stripRid(merged), column));
        merged = { ...merged, ...updated };
      } catch {
        // Parent surfaces the error; still apply the picked value.
      }
    }
    node.setData({ ...merged, [RID]: rid });
    recordEdit({ rid, before, after: { ...node.data } });
    if (enableSelection) { node.setSelected(true); emitSelection(); }
    emitAllRows();
  }, [recalcRow, stripRid, recordEdit, enableSelection, emitSelection, emitAllRows]);

  // Restore every row to its original preview state.
  const revertAll = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const updates: Record<string, unknown>[] = [];
    originalByRidRef.current.forEach((orig, rid) => updates.push({ ...orig, [RID]: rid }));
    api.applyTransaction({ update: updates });
    undoRef.current = [];
    redoRef.current = [];
    setHistVersion((v) => v + 1);
    if (enableSelection) emitSelection();
    emitAllRows();
  }, [enableSelection, emitSelection, emitAllRows]);

  const onCellValueChanged = useCallback(async (e: CellValueChangedEvent) => {
    // Editing a cell auto-selects its row (per original behavior) so it lands
    // in the persisted set; setSelected fires onSelectionChanged which feeds
    // the selected-rows path. emitAllRows covers the edit-without-selection
    // case (columnEditing on, MultiRowSelect off).
    if (enableSelection) e.node.setSelected(true);

    const editedCol = e.colDef && 'field' in e.colDef ? e.colDef.field : undefined;
    const rid = e.node?.data ? (e.node.data[RID] as number) : undefined;
    // Pre-edit snapshot: node.data has only this cell changed so far, so put
    // the field back to its old value to capture the full prior row.
    const before: Record<string, unknown> = {
      ...e.node.data,
      ...(editedCol ? { [editedCol]: e.oldValue } : {}),
    };

    // Recalculate the row so an edit propagates into dependent calc columns.
    if (recalcRow && editedCol && e.node?.data) {
      try {
        const updated = applyProtect(rid, await recalcRow(stripRid(e.node.data), editedCol));
        e.node.setData({ ...e.node.data, ...updated });
        if (rid != null) gridApiRef.current?.refreshCells({ rowNodes: [e.node], force: true });
        if (enableSelection) emitSelection();
      } catch {
        // Leave the manual edit in place if recalc fails; emitAllRows below
        // still surfaces it. The parent shows the error.
      }
    }
    if (rid != null) recordEdit({ rid, before, after: { ...e.node.data } });
    emitAllRows();
  }, [enableSelection, emitAllRows, emitSelection, recalcRow, stripRid, recordEdit]);

  // Open the trace/explainer panel for a calc cell (shared by the hover icon
  // and, for read-only calc columns, a plain cell click).
  const openTrace = useCallback((rowIndex: number, column: string) => {
    const t = traces?.[String(rowIndex)]?.[column];
    if (!t) return;
    setTraceStack([{ rowIndex, column, trace: t }]);
  }, [traces]);

  // Copy the current range (or, with no range, the focused cell) to the
  // clipboard as TSV — the Excel-compatible format (tabs between columns,
  // newlines between rows). Works on read-only grids too.
  const copyRange = useCallback(async () => {
    const api = gridApiRef.current;
    if (!api) return;
    let b: RangeBounds | null = rangeRef.current ? normRange(rangeRef.current) : null;
    if (!b) {
      const fc = api.getFocusedCell?.();
      const ci = fc ? columns.indexOf(fc.column?.getColId?.()) : -1;
      if (!fc || ci < 0) return;
      b = { r1: fc.rowIndex, r2: fc.rowIndex, c1: ci, c2: ci };
    }
    const lines: string[] = [];
    for (let r = b.r1; r <= b.r2; r++) {
      const node = api.getDisplayedRowAtIndex?.(r);
      const cells: string[] = [];
      for (let c = b.c1; c <= b.c2; c++) {
        const v = node?.data?.[columns[c]];
        cells.push(v == null ? '' : String(v));
      }
      lines.push(cells.join('\t'));
    }
    const tsv = lines.join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = tsv; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* clipboard unavailable */ }
    }
  }, [columns]);

  // Paste clipboard TSV into the grid starting at the range's (or focused cell's)
  // top-left, writing only into editable, unlocked cells. Each touched row is
  // recalculated once so downstream calc columns refresh.
  const pasteRange = useCallback(async () => {
    const api = gridApiRef.current;
    if (!api || !editingEnabled) return;
    let text = '';
    try { text = await navigator.clipboard.readText(); } catch { return; }
    if (!text) return;
    const matrix = text.replace(/\r/g, '').replace(/\n+$/, '').split('\n').map((l) => l.split('\t'));
    if (!matrix.length) return;

    let startR = -1; let startC = -1;
    if (rangeRef.current) { const b = normRange(rangeRef.current); startR = b.r1; startC = b.c1; }
    else {
      const fc = api.getFocusedCell?.();
      if (fc) { startR = fc.rowIndex; startC = columns.indexOf(fc.column?.getColId?.()); }
    }
    if (startR < 0 || startC < 0) return;

    const plans: {
      rid: number; node: IRowNode; before: Record<string, unknown>;
      updates: Record<string, unknown>; lastCol: string;
    }[] = [];
    for (let i = 0; i < matrix.length; i++) {
      const node = api.getDisplayedRowAtIndex?.(startR + i);
      if (!node?.data) continue;
      const rid = node.data[RID] as number;
      const before = { ...node.data };
      const updates: Record<string, unknown> = {};
      let lastCol = '';
      for (let j = 0; j < matrix[i].length; j++) {
        const col = columns[startC + j];
        if (!col) continue;
        const ed = isColEditable(col);
        if (!ed.editable) continue;
        if (protectRef.current.get(rid)?.has(col.toLowerCase())) continue;
        const t = (ed.type || '').toLowerCase();
        const isNum = ['int', 'integer', 'bigint', 'decimal', 'float', 'number', 'numeric'].includes(t)
          || numericSet.has(col);
        updates[col] = isNum ? toNumOrRaw(matrix[i][j]) : matrix[i][j];
        lastCol = col;
      }
      if (lastCol) plans.push({ rid, node, before, updates, lastCol });
    }
    if (!plans.length) return;

    const RECALC_CAP = 500;             // keep a big paste from firing 1000s of recalcs
    const doRecalc = !!recalcRow && plans.length <= RECALC_CAP;
    for (const p of plans) {
      let merged: Record<string, unknown> = { ...p.node.data, ...p.updates };
      if (doRecalc) {
        try {
          const updated = applyProtect(p.rid, await recalcRow!(stripRid(merged), p.lastCol));
          merged = { ...merged, ...updated };
        } catch { /* keep the raw paste if recalc fails */ }
      }
      p.node.setData({ ...merged, [RID]: p.rid });
      recordEdit({ rid: p.rid, before: p.before, after: { ...p.node.data } });
    }
    gridApiRef.current?.refreshCells({ force: true });
    if (enableSelection) emitSelection();
    emitAllRows();
  }, [columns, editingEnabled, isColEditable, numericSet, recalcRow, applyProtect,
    stripRid, recordEdit, enableSelection, emitSelection, emitAllRows]);

  // Paint the range highlight onto the live cell DOM. AG Grid virtualises rows,
  // so this re-runs on every range change and on body scroll (see onBodyScroll).
  useEffect(() => {
    const root = gridBoxRef.current;
    if (!root) return;
    let b: RangeBounds | null = null;
    if (rangeRef.current) {
      const n = normRange(rangeRef.current);
      if (!(n.r1 === n.r2 && n.c1 === n.c2)) b = n;   // ignore a single-cell "range"
    }
    root.querySelectorAll('.ag-cell').forEach((cellEl) => {
      const el = cellEl as HTMLElement;
      if (!b) { el.classList.remove('gp-range'); return; }
      const colId = el.getAttribute('col-id');
      const ci = colId ? columns.indexOf(colId) : -1;
      const rowEl = el.closest('.ag-row');
      const ri = rowEl ? Number(rowEl.getAttribute('row-index')) : NaN;
      const inRange = ci >= b.c1 && ci <= b.c2 && !Number.isNaN(ri) && ri >= b.r1 && ri <= b.r2;
      el.classList.toggle('gp-range', inRange);
    });
  }, [rangeVersion, columns]);

  // Mouse drag = rectangular range select (Excel-style). Attached to the grid
  // root; the move/up listeners live on document so a drag can leave the grid.
  useEffect(() => {
    const root = gridBoxRef.current;
    if (!root) return;
    const cellAt = (el: HTMLElement | null): { ri: number; ci: number } | null => {
      const cell = el?.closest('.ag-cell') as HTMLElement | null;
      if (!cell) return null;
      const colId = cell.getAttribute('col-id');
      const ci = colId ? columns.indexOf(colId) : -1;
      const rowEl = cell.closest('.ag-row');
      const ri = rowEl ? Number(rowEl.getAttribute('row-index')) : NaN;
      if (ci < 0 || Number.isNaN(ri)) return null;
      return { ri, ci };
    };
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const onDown = (e: any) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement;
      // Ignore clicks on action icons, an open cell editor, or the header.
      if (t.closest('button') || t.closest('input,textarea,select') || t.closest('.ag-header')) return;
      const hit = cellAt(t);
      if (!hit) return;
      rangeRef.current = { a: { r: hit.ri, c: hit.ci }, f: { r: hit.ri, c: hit.ci } };
      draggingRef.current = true;
      bumpRange();
    };
    const onMove = (e: any) => {
      if (!draggingRef.current || !rangeRef.current) return;
      const hit = cellAt(document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null);
      if (!hit) return;
      const cur = rangeRef.current.f;
      if (cur.r !== hit.ri || cur.c !== hit.ci) {
        rangeRef.current = { a: rangeRef.current.a, f: { r: hit.ri, c: hit.ci } };
        window.getSelection?.()?.removeAllRanges?.();
        bumpRange();
      }
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */
    const onUp = () => { draggingRef.current = false; };
    root.addEventListener('mousedown', onDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      root.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [columns, bumpRange]);

  // Keyboard: Ctrl/Cmd+C copy range, Ctrl/Cmd+V paste (edit mode), Ctrl/Cmd+Z/Y
  // undo/redo, Esc clears the range. Ignored while a cell editor is open so the
  // editor handles in-field text editing.
  const onGridKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && rangeRef.current) { clearRange(); return; }
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    if (gridApiRef.current?.getEditingCells()?.length) return;
    const key = e.key.toLowerCase();
    if (key === 'c') { e.preventDefault(); void copyRange(); return; }
    if (key === 'v' && editingEnabled) { e.preventDefault(); void pasteRange(); return; }
    if (!editingEnabled) return;
    if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
    else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); }
  }, [editingEnabled, undo, redo, copyRange, pasteRange, clearRange]);

  // Stable context object for the cell renderers.
  const gridContext = useMemo(
    () => ({ openTrace, revertCell, openLookup, getOriginal, valuesEqual }),
    [openTrace, revertCell, openLookup, getOriginal],
  );

  // Re-derive on each history change so the toolbar buttons enable/disable.
  const { canUndo, canRedo } = useMemo(
    () => ({ canUndo: undoRef.current.length > 0, canRedo: redoRef.current.length > 0 }),
    [histVersion],
  );

  const colDefs: ColDef[] = useMemo(
    () => {
      const dataCols = columns.map<ColDef>((col) => {
      const isCalc = calcSet.has(col.toLowerCase());
      const isNumeric = numericSet.has(col);
      const edit = isColEditable(col);
      // Editable + has a reference-table lookup → use the editor that keeps the
      // 🔍 reachable WHILE editing the cell.
      const useLookupEditor = edit.editable && !!edit.lookup && !!fetchLookup;
      const headerClasses = [
        isCalc ? 'gp-calc-header' : '',
        isNumeric ? 'gp-numeric-header' : '',
        edit.editable ? 'gp-editable-header' : '',
      ].filter(Boolean).join(' ');
      // A user-resized column gets a fixed width; un-resized columns flex to
      // fill the grid. (flex and width are mutually exclusive in AG Grid.)
      const storedWidth = columnWidths?.[col];
      const sizing: Partial<Pick<ColDef, 'width' | 'flex'>> = typeof storedWidth === 'number'
        ? { width: storedWidth }
        : { flex: 1 };
      // Custom heading (source override or merged calc header) wins; otherwise
      // fall back to the humanized field name.
      const headerName = columnHeaders?.[col]?.trim() || humanizeHeader(col);
      return {
        field: col,
        headerName,
        headerComponent: GpColumnHeader,
        ...sizing,
        headerTooltip: edit.editable ? `${col} (editable)` : col,
        sortable: true,
        filter: true,
        resizable: true,
        // Editable unless an item_change has locked this cell (protect).
        editable: edit.editable
          ? (p: { data?: Record<string, unknown> }) =>
              !(protectRef.current.get(p.data?.[RID] as number)?.has(col.toLowerCase()))
          : false,
        cellEditor: edit.editable
          ? (useLookupEditor ? LookupCellEditor : editorFor(edit.type))
          : undefined,
        cellEditorParams: useLookupEditor ? { numeric: isNumeric } : undefined,
        // Calc cells get a hover "explain" icon (works even when editable);
        // editable cells get a "revert to original" icon once changed.
        cellRenderer: (isCalc && traces) || edit.editable ? GridCellRenderer : undefined,
        cellRendererParams: {
          explain: isCalc && !!traces,
          editable: edit.editable,
          hasLookup: edit.editable && !!edit.lookup && !!fetchLookup,
        },
        type: isNumeric ? 'numericColumn' : undefined,
        cellClass: [
          isNumeric ? 'gp-numeric-cell' : '',
          isCalc ? 'gp-calc-cell' : '',
          edit.editable ? 'gp-editable-cell' : '',
        ].filter(Boolean).join(' ') || undefined,
        valueFormatter: (p: { value: unknown }) => (isNumeric ? formatNumber(p.value) : fmt(p.value)),
        cellStyle: (p: {
          value: unknown; data?: Record<string, unknown>;
          node?: { rowIndex?: number | null };
        }) => {
          const styles: Record<string, string> = {
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '13px',
          };
          if (isNumeric) {
            styles.textAlign = 'right';
            styles.fontVariantNumeric = 'tabular-nums';
          }
          if (isCalc) {
            // Background comes from the .gp-calc-cell class (not inline) so the
            // row hover / focus CSS can override it by specificity. Editable
            // calc cells use a text cursor (click = edit); read-only ones a
            // pointer (click = explain).
            styles.cursor = edit.editable ? 'text' : (traces ? 'pointer' : 'default');
          }
          // Highlight cells whose value differs from the previous snapshot.
          if (prevByKey) {
            const i = p.node?.rowIndex ?? -1;
            if (i >= 0 && i < prevByKey.length) {
              const before = prevByKey[i]?.[col];
              if (fmt(before) !== fmt(p.value)) {
                styles.background = isCalc ? '#fff4d6' : '#e3f2fd';
                styles.borderLeft = '2px solid #1976d2';
              }
            }
          }
          // item_change-locked cell (protect) — greyed + read-only look.
          if (edit.editable
              && protectRef.current.get(p.data?.[RID] as number)?.has(col.toLowerCase())) {
            styles.background = 'rgba(120,120,120,0.16)';
            styles.color = 'var(--color-text-secondary, #888)';
          }
          // Validation error/warning cell — red highlight (wins, set last).
          // Keyed by the ORIGINAL preview-row index (RID) so sort/filter can't
          // shift the highlight off the offending row.
          const ri = (p.data?.[RID] as number);
          if (errorCells && ri != null && errorCells.has(`${ri}:${col.toLowerCase()}`)) {
            styles.background = 'rgba(239,68,68,0.18)';
            styles.boxShadow = 'inset 0 0 0 1px rgba(239,68,68,0.75)';
          }
          return styles;
        },
        headerClass: headerClasses || undefined,
      };
      });
      // The checkbox selection column is rendered by AG Grid's row-selection
      // API (see `rowSelection` on the grid) — no manual `__select` column.
      return dataCols;
    },
    [columns, calcSet, traces, prevByKey, numericSet, isColEditable, enableSelection,
      fetchLookup, columnWidths, columnHeaders, errorCells],
  );

  const onCellClicked = (e: CellClickedEvent) => {
    const col = e.colDef.field;
    const idx = e.node?.rowIndex;
    if (!col || idx == null) return;
    if (!calcSet.has(col.toLowerCase())) return;
    // For editable calc cells, a plain click should focus/edit — the explainer
    // is opened from the hover icon instead. Read-only calc cells keep
    // click-to-explain.
    if (isColEditable(col).editable) return;
    openTrace(idx, col);
  };

  // Resolve a calc input -> drill target. Prefer a recorded calc trace
  // (chained drill). Fall back to a synthetic 'R' trace so raw source
  // columns are clickable too -- the panel renders a small "fetched from
  // source SQL" notice with the row value, so every variable in every
  // calculation (including lookups) supports drill-down.
  const resolveDrillTrace = useCallback(
    (rowIndex: number, inputName: string): CellTrace | null => {
      const want = inputName.toLowerCase();

      // 1) chained calc trace (same row)
      const rowTraces = traces?.[String(rowIndex)];
      if (rowTraces) {
        if (rowTraces[inputName]) return rowTraces[inputName];
        for (const k of Object.keys(rowTraces)) {
          if (k.toLowerCase() === want) return rowTraces[k];
        }
      }

      // 2) raw source column -- synthesize a read-only "source column" trace
      const row = rows?.[rowIndex];
      if (!row) return null;
      let value: unknown = undefined;
      if (inputName in row) {
        value = row[inputName];
      } else {
        for (const k of Object.keys(row)) {
          if (k.toLowerCase() === want) { value = row[k]; break; }
        }
      }
      if (value === undefined) return null;
      return {
        calc_type: 'R',
        title: 'Source column',
        expression: '',
        result: value,
        inputs: {},
      };
    },
    [traces, rows],
  );

  const pushTrace = useCallback((rowIndex: number, column: string, trace: CellTrace) => {
    setTraceStack((s) => [...s, { rowIndex, column, trace }]);
  }, []);

  const popTrace = useCallback(() => {
    setTraceStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const jumpTrace = useCallback((depth: number) => {
    setTraceStack((s) => (depth >= 0 && depth < s.length ? s.slice(0, depth + 1) : s));
  }, []);

  // Persist a user drag-resize: only fire once the drag ends (``finished``) and
  // only for user-initiated resizes (ignore programmatic/auto sizing, which
  // would otherwise pin every column at its current computed width). Merge the
  // affected columns into the existing width map so unrelated columns are kept.
  const onColumnResized = useCallback((e: ColumnResizedEvent) => {
    if (!onColumnWidthsChange || !e.finished) return;
    if (e.source !== 'uiColumnResized' && e.source !== 'uiColumnDragged') return;
    const next = { ...(columnWidths || {}) };
    let changed = false;
    for (const c of e.columns || []) {
      const id = c.getColId();
      if (id === '__select') continue;
      next[id] = c.getActualWidth();
      changed = true;
    }
    if (changed) onColumnWidthsChange(next);
  }, [onColumnWidthsChange, columnWidths]);

  return (
    <Box
      sx={fullscreen ? {
        position: 'fixed', inset: 0, zIndex: (t) => t.zIndex.modal,
        bgcolor: 'background.paper', p: 2,
        display: 'flex', flexDirection: 'column', overflow: 'auto',
      } : undefined}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {rowCount.toLocaleString()} row(s){' '}
          · showing {visibleCount.toLocaleString()}
          {visibleCount !== rows.length && (
            <> of {rows.length.toLocaleString()}</>
          )}
        </Typography>
        <Tooltip title="Manage row filters">
          <IconButton
            size="small"
            color={activeFilters.length ? 'primary' : 'default'}
            onClick={() => setFiltersDialogOpen(true)}
            aria-label="manage filters"
          >
            <Badge color="primary" badgeContent={activeFilters.length} overlap="circular">
              <FilterAltIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="Column headings & widths">
          <IconButton
            size="small"
            onClick={() => setColumnsDialogOpen(true)}
            aria-label="column settings"
          >
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}>
          <IconButton
            size="small"
            color={fullscreen ? 'primary' : 'default'}
            onClick={() => setFullscreen((f) => !f)}
            aria-label={fullscreen ? 'exit full screen' : 'full screen'}
          >
            {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        {editingEnabled && (
          <>
            <Tooltip title="Undo (Ctrl/Cmd+Z)">
              <span>
                <IconButton size="small" onClick={undo} disabled={!canUndo} aria-label="undo edit">
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo (Ctrl/Cmd+Shift+Z)">
              <span>
                <IconButton size="small" onClick={redo} disabled={!canRedo} aria-label="redo edit">
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Revert all edited cells to their original values">
              <span>
                <Button
                  size="small" color="inherit"
                  startIcon={<SettingsBackupRestoreIcon />}
                  onClick={revertAll}
                  disabled={!canUndo && !canRedo}
                >
                  Revert all
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        {filters.map((f) => (
          <Tooltip
            key={f.name}
            title={f.expression || '(empty expression)'}
          >
            <Chip
              size="small"
              color={f.enabled ? 'primary' : 'default'}
              variant={f.enabled ? 'filled' : 'outlined'}
              label={f.name}
              onClick={() => toggleFilter(f.name)}
              sx={{ maxWidth: 220, opacity: f.enabled ? 1 : 0.7 }}
            />
          </Tooltip>
        ))}
        {calcColumns && calcColumns.length > 0 && (
          <Chip size="small" variant="outlined" sx={{ bgcolor: '#fffbe6' }}
            label={`${calcColumns.length} calculated column(s)`} />
        )}
        {traces && (
          <Chip size="small" color="info" variant="outlined" label="Click a yellow cell to see why" />
        )}
        {previousRows && (
          <Chip size="small" variant="outlined" sx={{ borderColor: '#1976d2' }}
            label="Blue = changed vs previous preview" />
        )}
        <Chip
          size="small" variant="outlined"
          label={editingEnabled
            ? 'Drag to select · Ctrl/⌘+C copy · Ctrl/⌘+V paste'
            : 'Drag to select · Ctrl/⌘+C copy'}
        />
      </Stack>

      {filterErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }}>
          {filterErrors.join(' · ')}
        </Alert>
      )}
      {filterErrors.length === 0 && filterWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
          {filterWarnings.join(' · ')}
        </Alert>
      )}

      <FiltersManagerDialog
        open={filtersDialogOpen}
        filters={filters}
        columns={columns}
        numericColumns={numericSet}
        distinctValues={distinctValues}
        onClose={() => setFiltersDialogOpen(false)}
        onChange={updateFilters}
      />

      <ColumnSettingsDialog
        open={columnsDialogOpen}
        columns={columns}
        calcColumns={calcSet}
        headers={columnHeaders || {}}
        widths={columnWidths || {}}
        humanize={humanizeHeader}
        onClose={() => setColumnsDialogOpen(false)}
        onChangeHeader={(field, header) => onColumnHeaderChange?.(field, header)}
        onChangeWidth={setColumnWidth}
        onEditCalc={onEditCalc}
      />

      <Box
        ref={gridBoxRef}
        data-ag-theme-mode={
          typeof document !== 'undefined'
            && document.body.classList.contains('light-theme') ? 'light' : 'dark'
        }
        onKeyDown={onGridKeyDown}
        sx={{
          width: '100%',
          ...(fullscreen ? { flex: 1, minHeight: 0 } : { height: 480 }),
          // Let the grid scroll (vertically and horizontally) when content
          // overflows rather than clipping it.
          overflow: 'hidden',
          '& .ag-root-wrapper': { height: '100%' },
          '& .gp-calc-header': { background: '#fff7c6' },
          '& .gp-calc-cell': { background: '#fffbe6' },
          '& .gp-numeric-header .ag-header-cell-label': { justifyContent: 'flex-end' },
          '& .gp-numeric-cell': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
          // Editable columns: subtle green tint + pencil affordance so users
          // can tell which cells they may change before persisting.
          '& .gp-editable-header': { background: '#e6f4ea' },
          '& .gp-editable-cell': { background: '#f1f8f3', cursor: 'text' },
          '& .ag-row-hover .gp-editable-cell': { background: 'transparent !important' },
          // Calc cell layout: value + a hover-only "explain" icon at the right.
          '& .gp-calc-cellwrap': {
            display: 'flex', alignItems: 'center', width: '100%', gap: '4px',
          },
          '& .gp-calc-cellwrap .gp-cell-val': {
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .gp-numeric-cell .gp-calc-cellwrap .gp-cell-val': { textAlign: 'right' },
          '& .gp-explain-btn, & .gp-revert-btn, & .gp-lookup-btn': {
            flex: '0 0 auto', display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', border: 'none', background: 'transparent',
            cursor: 'pointer', padding: 0, opacity: 0, transition: 'opacity 120ms',
          },
          '& .gp-explain-btn': { color: '#1976d2' },
          '& .gp-lookup-btn': { color: '#1976d2' },
          // The revert icon stays visible (not only on hover) so changed cells
          // are obvious at a glance.
          '& .gp-revert-btn': { color: '#b26a00', opacity: 0.9 },
          '& .ag-cell:hover .gp-explain-btn, & .ag-cell:hover .gp-lookup-btn': { opacity: 0.85 },
          '& .gp-explain-btn:hover, & .gp-revert-btn:hover, & .gp-lookup-btn:hover': { opacity: 1 },
          // On hover, clear the calc cell's yellow so the row hover tint shows
          // through — keeps highlighting consistent across all columns.
          '& .ag-row-hover .gp-calc-cell': { background: 'transparent !important' },
          '& .ag-row-hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04) !important',
          },
          // Highlight the individual cell under the cursor, on top of the row
          // tint, so the exact cell being inspected stands out.
          '& .ag-cell:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.16) !important',
            boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.55)',
          },
          // Drag-select cell range (Excel-style). Native text selection is off so
          // a drag selects cells, not text — our Ctrl/⌘+C handles copy.
          '& .ag-cell': { userSelect: 'none' },
          // …but keep text selectable inside an open cell editor.
          '& .ag-cell input, & .ag-cell textarea': { userSelect: 'text' },
          '& .ag-cell.gp-range': {
            backgroundColor: 'rgba(25, 118, 210, 0.14) !important',
            boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.35)',
          },
          // Custom header: sort glyph + a filter funnel revealed on header hover.
          '& .gp-h-sort': { fontSize: '13px', color: '#1976d2', lineHeight: 1 },
          '& .gp-h-filter': {
            opacity: 0, border: 'none', background: 'transparent', cursor: 'pointer',
            color: 'inherit', display: 'inline-flex', alignItems: 'center', padding: 0,
            transition: 'opacity 120ms',
          },
          '& .ag-header-cell:hover .gp-h-filter, & .ag-header-cell:focus-within .gp-h-filter': {
            opacity: 0.6,
          },
          '& .gp-h-filter:hover': { opacity: 1, color: '#1976d2' },
          '& .gp-h-filter.active': { opacity: 1, color: '#1976d2' },
          // In-editor lookup button.
          '& .gp-lk-ed-btn': {
            flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', border: 'none',
            background: 'transparent', color: '#1976d2', cursor: 'pointer', padding: '0 6px',
          },
          '& .gp-lk-ed-btn:hover': { color: '#1565c0' },
        }}
      >
        <AgGridReact
          theme={themeQuartz}
          rowData={gridRows}
          columnDefs={colDefs}
          getRowId={(p: { data: Record<string, unknown> }) => String(p.data[RID])}
          onCellClicked={onCellClicked}
          onGridReady={(e: { api: GridApi }) => { gridApiRef.current = e.api; }}
          onColumnResized={onColumnResized}
          onBodyScroll={() => { if (rangeRef.current) bumpRange(); }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onFilterChanged={onFilterChanged}
          rowSelection={enableSelection ? {
            mode: 'multiRow',
            checkboxes: true,
            headerCheckbox: true,
            // Select-all targets only the rows surviving the external filter.
            selectAll: 'filtered',
            // Selection happens via the checkbox column, not by clicking a row
            // (cells stay clickable for the trace/edit affordances).
            enableClickSelection: false,
          } : undefined}
          onSelectionChanged={enableSelection ? emitSelection : undefined}
          onCellValueChanged={editingEnabled ? onCellValueChanged : undefined}
          stopEditingWhenCellsLoseFocus
          context={gridContext}
          animateRows
          defaultColDef={{
            minWidth: 110, sortable: true, filter: true,
            resizable: true, editable: false,
          }}
          // Keep cells focusable so arrow keys navigate the grid (and Enter/F2
          // start editing on editable cells).
          suppressCellFocus={false}
          ensureDomOrder
        />
      </Box>

      {activeTrace && (() => {
        const calcIdx = calcIndexByColumn ? calcIndexByColumn(activeTrace.column) : -1;
        const hasActions = calcIdx >= 0 && !!onEditCalc;
        return (
          <TracePanel
            rowIndex={activeTrace.rowIndex}
            column={activeTrace.column}
            trace={activeTrace.trace}
            breadcrumbs={traceStack.map((f) => ({ column: f.column, rowIndex: f.rowIndex }))}
            onJumpDepth={jumpTrace}
            onBack={traceStack.length > 1 ? popTrace : undefined}
            resolveDrillTrace={(name) => resolveDrillTrace(activeTrace.rowIndex, name)}
            onDrillInto={(name, t) => pushTrace(activeTrace.rowIndex, name, t)}
            humanize={humanizeHeader}
            onClose={() => setTraceStack([])}
            canEdit={hasActions}
            canMoveUp={hasActions && calcIdx > 0}
            canMoveDown={hasActions && calcCount != null && calcIdx < calcCount - 1}
            onEditCalc={() => onEditCalc?.(activeTrace.column)}
            onDeleteCalc={() => onDeleteCalc?.(activeTrace.column)}
            onMoveCalc={(d) => onMoveCalc?.(activeTrace.column, d)}
          />
        );
      })()}

      {lookupTarget && fetchLookup && (
        <LookupPickerDialog
          open
          column={lookupTarget.column}
          table={lookupTarget.lookup.table}
          currentValue={lookupTarget.current}
          search={(text) => fetchLookup(lookupTarget.column, text, lookupTarget.row)}
          onClose={() => setLookupTarget(null)}
          onSelect={(row) => {
            void applyLookupValue(lookupTarget.rid, lookupTarget.column, row.value);
          }}
        />
      )}
    </Box>
  );
}
